/**
 * CallScorePanel.jsx — Per-call agent performance score (Phase 2.2).
 *
 * Iter 164 (Josh — Danny Phase 2.2, CORRECTED)
 *
 * Renders the 6-dimension Agent Call Score (0-100) on the Call Review
 * slide-out. Six dimensions per spec §2.2:
 *   • Engagement (agent) · 20%
 *   • Barrier navigation · 20%
 *   • Authority & confidence · 20%
 *   • Tone · 15%
 *   • Product knowledge · 15%
 *   • Prompt adherence · 10%
 *
 * Composite score (0-100) is band-coloured per spec §2.2(G):
 *   85-100 Excellent · 70-84 Good · 55-69 Developing ·
 *   40-54 Needs improvement · 0-39 Critical.
 *
 * Manager-only fields (barrier coaching notes, product-knowledge
 * inaccuracy corrections) are gated via the `isManager` prop.
 *
 * Server endpoint: GET /api/calls/{activity_id}/score
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth, API } from "@/App";
import {
  Sparkles, Loader2, AlertTriangle, CheckCircle2, XCircle, MinusCircle,
  RefreshCw, Mic, Shield, Volume2, BookOpen, ListChecks, Heart,
} from "lucide-react";

const DIM_META = {
  engagement_agent:     { label: "Engagement (agent)", icon: Mic,        cap: 20 },
  barrier_navigation:   { label: "Barrier navigation", icon: Shield,     cap: 20 },
  authority_confidence: { label: "Authority & confidence", icon: Sparkles, cap: 20 },
  tone:                 { label: "Tone",               icon: Volume2,    cap: 15 },
  product_knowledge:    { label: "Product knowledge",  icon: BookOpen,   cap: 15 },
  prompt_adherence:     { label: "Prompt adherence",   icon: ListChecks, cap: 10 },
};
const DIM_ORDER = ["engagement_agent", "barrier_navigation", "authority_confidence", "tone", "product_knowledge", "prompt_adherence"];

const NAV_BADGE = {
  "Overcome":            { bg: "#dcfce7", color: "#166534", icon: CheckCircle2 },
  "Partially addressed": { bg: "#fef3c7", color: "#854d0e", icon: AlertTriangle },
  "Avoided":             { bg: "#fee2e2", color: "#991b1b", icon: MinusCircle },
  "Not addressed":       { bg: "#fee2e2", color: "#7f1d1d", icon: XCircle },
};

export default function CallScorePanel({ activityId, isManager = false, onScored = null }) {
  const { token } = useAuth();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [rescoring, setRescoring] = useState(false);

  useEffect(() => {
    if (!activityId || !token) return;
    let cancel = false;
    setState({ loading: true, data: null, error: null });
    axios
      .get(`${API}/calls/${activityId}/score`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!cancel) { setState({ loading: false, data: r.data, error: null }); onScored?.(r.data); } })
      .catch((e) => { if (!cancel) setState({ loading: false, data: null, error: e?.response?.data?.detail || e.message }); });
    return () => { cancel = true; };
  }, [activityId, token, onScored]);

  const rescore = async () => {
    setRescoring(true);
    try {
      const r = await axios.post(`${API}/calls/${activityId}/score`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setState({ loading: false, data: r.data, error: null });
      onScored?.(r.data);
    } catch (e) {
      setState((s) => ({ ...s, error: e?.response?.data?.detail || e.message }));
    } finally {
      setRescoring(false);
    }
  };

  if (state.loading) {
    return (
      <div data-testid="call-score-loading" style={{ padding: 14, display: "inline-flex", alignItems: "center", gap: 8, color: "#64748b", fontSize: 12 }}>
        <Loader2 style={{ width: 14, height: 14, animation: "spin 1s linear infinite" }} /> Scoring this call across 6 dimensions…
      </div>
    );
  }
  if (state.error) {
    const code = typeof state.error === "object" ? state.error?.code : null;
    const msg = typeof state.error === "object" ? state.error?.message : state.error;
    return (
      <div data-testid="call-score-error" style={{ display: "flex", gap: 8, padding: 12, background: code === "no_transcript" ? "#f1f5f9" : "#fef3c7", borderRadius: 8, fontSize: 12, color: code === "no_transcript" ? "#475569" : "#92400e" }}>
        <AlertTriangle style={{ width: 14, height: 14, flexShrink: 0, marginTop: 1 }} />
        <span>{msg || "Failed to score this call."}</span>
      </div>
    );
  }
  const score = state.data?.score || state.data;   // GET returns {score: {...}}, POST returns the full thing
  if (!score) return null;
  const composite = score.composite ?? 0;
  const bandColor = score.band_color || "#64748b";

  return (
    <div data-testid="call-score-panel" style={{ background: "#fff", border: "0.5px solid var(--border, #e2e8f0)", borderRadius: 12, overflow: "hidden", fontFamily: "inherit" }}>
      {/* Composite header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", borderBottom: "0.5px solid #e2e8f0", background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)" }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
          <Sparkles style={{ width: 14, height: 14, color: "#6366f1" }} />
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.08em" }}>Agent Call Score</div>
            <div style={{ fontSize: 11, color: "#475569", marginTop: 1 }}>6-dimension framework · {score.cached ? "cached" : "fresh"}</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {isManager && (
            <button
              data-testid="call-score-rescore"
              onClick={rescore}
              disabled={rescoring}
              title="Force re-score"
              style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "transparent", border: "0.5px solid #cbd5e1", borderRadius: 6, padding: "4px 9px", fontSize: 10, fontWeight: 600, cursor: rescoring ? "wait" : "pointer", color: "#475569", fontFamily: "inherit" }}
            >
              <RefreshCw style={{ width: 10, height: 10, animation: rescoring ? "spin 1s linear infinite" : "none" }} />
              {rescoring ? "Scoring…" : "Re-score"}
            </button>
          )}
          <div data-testid="call-score-composite" style={{ textAlign: "right" }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>Composite</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: bandColor, lineHeight: 1.05 }}>{composite}<span style={{ fontSize: 12, color: "#94a3b8", fontWeight: 500 }}>/100</span></div>
            <div style={{ fontSize: 10, fontWeight: 700, color: bandColor, textTransform: "uppercase", letterSpacing: "0.06em" }}>{score.band_label}</div>
          </div>
        </div>
      </div>

      {/* Lead engagement (separate from agent, per spec §2.2(A)) */}
      {typeof score.lead_engagement_1_5 === "number" && (
        <div style={{ padding: "10px 16px", borderBottom: "0.5px solid #e2e8f0", display: "flex", alignItems: "center", gap: 10, background: "#fafbfc" }}>
          <Heart style={{ width: 12, height: 12, color: "#dc2626" }} />
          <span style={{ fontSize: 11, fontWeight: 600, color: "#0f172a" }}>Lead engagement:</span>
          <span data-testid="call-score-lead-engagement" style={{ fontSize: 11, color: "#475569" }}>
            {score.lead_engagement_1_5}/5 · <em>{score.lead_engagement_label}</em>
          </span>
        </div>
      )}

      {/* Dimension grid. Iter 176.x — auto-fit at 150px min so the
          grid collapses to a single column inside narrow contexts
          (e.g. the 340px Call Review left panel) instead of clipping
          dimension cards. */}
      <div style={{ padding: 14, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
        {DIM_ORDER.map((k) => {
          const meta = DIM_META[k];
          const dim = score.dimensions?.[k] || {};
          const sc = dim.score ?? 0;
          const pct = (sc / meta.cap) * 100;
          const color = pct >= 75 ? "#059669" : pct >= 50 ? "#d97706" : "#dc2626";
          const Icon = meta.icon;
          return (
            <div key={k} data-testid={`call-score-dim-${k}`} style={{ padding: "10px 12px", border: "0.5px solid #e2e8f0", borderRadius: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                <Icon style={{ width: 11, height: 11, color: "#475569" }} />
                <div style={{ fontSize: 11, fontWeight: 600, color: "#0f172a", flex: 1 }}>{meta.label}</div>
                <div style={{ fontSize: 12, fontWeight: 700, color, fontVariantNumeric: "tabular-nums" }}>{sc}<span style={{ fontSize: 9, color: "#94a3b8", fontWeight: 500 }}>/{meta.cap}</span></div>
              </div>
              <div style={{ height: 3, background: "#f1f5f9", borderRadius: 2, overflow: "hidden", marginBottom: 6 }}>
                <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 2 }} />
              </div>
              {dim.label && (
                <div style={{ fontSize: 10, color: "#475569", fontWeight: 500, marginBottom: 4 }}>{dim.label}</div>
              )}
              {dim.rationale && (
                <div style={{ fontSize: 10, color: "#64748b", lineHeight: 1.4 }}>{dim.rationale}</div>
              )}
              {/* Tone & authority quotes */}
              {Array.isArray(dim.quotes) && dim.quotes.length > 0 && (
                <div style={{ marginTop: 5, paddingLeft: 6, borderLeft: "1.5px solid #cbd5e1" }}>
                  {dim.quotes.slice(0, 2).map((q, i) => (
                    <div key={i} style={{ fontSize: 10, color: "#475569", fontStyle: "italic", lineHeight: 1.35, marginBottom: 2 }}>"{q}"</div>
                  ))}
                </div>
              )}
              {/* Prompt adherence checklist */}
              {k === "prompt_adherence" && dim.checklist && (
                <div style={{ marginTop: 6, display: "grid", gridTemplateColumns: "1fr", gap: 3 }}>
                  {[
                    ["opening_value_prop", "Opened with value prop"],
                    ["qualification_questions", "Asked qualification Qs"],
                    ["next_step_set", "Set a next step"],
                  ].map(([key, label]) => (
                    <div key={key} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10 }}>
                      {dim.checklist[key] ? (
                        <CheckCircle2 style={{ width: 11, height: 11, color: "#059669" }} />
                      ) : (
                        <XCircle style={{ width: 11, height: 11, color: "#dc2626" }} />
                      )}
                      <span style={{ color: dim.checklist[key] ? "#0f172a" : "#94a3b8" }}>{label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Barrier-navigation detailed table */}
      {Array.isArray(score.dimensions?.barrier_navigation?.barriers) && score.dimensions.barrier_navigation.barriers.length > 0 && (
        <div data-testid="call-score-barriers" style={{ padding: "0 14px 14px" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
            Barriers raised & navigation
          </div>
          {score.dimensions.barrier_navigation.barriers.map((b, i) => {
            const badge = NAV_BADGE[b.navigation_quality] || { bg: "#f1f5f9", color: "#475569", icon: MinusCircle };
            const BadgeIcon = badge.icon;
            return (
              <div key={i} style={{ padding: 10, marginBottom: 8, border: "0.5px solid #e2e8f0", borderRadius: 8 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ fontSize: 10, fontWeight: 600, color: "#0f172a", textTransform: "uppercase", letterSpacing: "0.05em" }}>Barrier #{i + 1}</span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 999, fontSize: 9, fontWeight: 700, background: badge.bg, color: badge.color }}>
                    <BadgeIcon style={{ width: 10, height: 10 }} /> {b.navigation_quality}
                  </span>
                </div>
                <div style={{ fontSize: 10, color: "#64748b", marginBottom: 2 }}>Lead said:</div>
                <div style={{ fontSize: 11, color: "#0f172a", fontStyle: "italic", lineHeight: 1.4, paddingLeft: 6, borderLeft: "1.5px solid #fca5a5", marginBottom: 6 }}>"{b.barrier_quote}"</div>
                <div style={{ fontSize: 10, color: "#64748b", marginBottom: 2 }}>Agent responded:</div>
                <div style={{ fontSize: 11, color: "#0f172a", fontStyle: "italic", lineHeight: 1.4, paddingLeft: 6, borderLeft: "1.5px solid #93c5fd" }}>"{b.agent_response_quote}"</div>
                {isManager && b.coaching_note && (
                  <div style={{ marginTop: 6, padding: 8, background: "#fef3c7", borderRadius: 6, fontSize: 10, color: "#92400e", lineHeight: 1.4 }}>
                    <strong>Coaching note (manager-only):</strong> {b.coaching_note}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Product-knowledge inaccuracies (manager-only) */}
      {isManager && Array.isArray(score.dimensions?.product_knowledge?.inaccuracies) && score.dimensions.product_knowledge.inaccuracies.length > 0 && (
        <div data-testid="call-score-product-inaccuracies" style={{ padding: "0 14px 14px" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#7f1d1d", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
            Product-knowledge gaps (manager-only)
          </div>
          {score.dimensions.product_knowledge.inaccuracies.map((x, i) => (
            <div key={i} style={{ padding: 10, marginBottom: 6, background: "#fef2f2", borderRadius: 8, border: "0.5px solid #fecaca" }}>
              <div style={{ fontSize: 10, color: "#7f1d1d", marginBottom: 2 }}>Agent said:</div>
              <div style={{ fontSize: 11, color: "#7f1d1d", fontStyle: "italic", lineHeight: 1.4, marginBottom: 6 }}>"{x.agent_quote}"</div>
              <div style={{ fontSize: 10, color: "#0f172a", marginBottom: 2 }}>Correct info:</div>
              <div style={{ fontSize: 11, color: "#0f172a", lineHeight: 1.4 }}>{x.correction}</div>
            </div>
          ))}
        </div>
      )}

      {/* Overall summary */}
      {score.overall_call_summary && (
        <div data-testid="call-score-summary" style={{ padding: "0 14px 14px" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Overall</div>
          <div style={{ fontSize: 11, color: "#0f172a", lineHeight: 1.5, padding: 10, background: "#f8fafc", borderRadius: 8 }}>{score.overall_call_summary}</div>
          <div style={{ marginTop: 6, fontSize: 10, color: "#64748b", fontStyle: "italic" }}>{score.band_action}</div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
